import React from 'react';
import styles from './styles.module.css';
import mainStyles from '../WorkerManagementComponent/WorkerManagementComponent.module.css';
import {
  Container, Grid, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Backdrop, CircularProgress, Snackbar,
} from '@material-ui/core';
import { Add, DeleteForever } from '@material-ui/icons';
import CreateIcon from '@material-ui/icons/Create';
import { Alert } from '@material-ui/lab';
import Pagination from '@material-ui/lab/Pagination';
import { BlueInverseBtn } from '../Styled/StyledButtons';
import ConfirmComponent from '../ConfirmComponent/ConfirmComponent';
import { Pages } from '../../models/Pages';
import { CCPhone, ICCPhone, LineType } from '../../models/Phones/CCPhone';
import { PhonesActions, IAxiosCCPhones } from '../../actions/PhonesActions';
import PhoneEditComponent from './PhoneEditComponent';
import PhoneMaskInput from '../common/PhoneMaskInput';

interface IState {
  phones: CCPhone[];
  pagination: Pages | null;
  backdrop: boolean;
  alert: boolean;
  errorAlert: boolean;
  errors: string[];
  filter: PhonesFilter;
  searchDisplay: string;
  editShow: boolean;
  currentPhone: CCPhone;
  deleteConfirmShow: boolean;
  deletePhoneId: number;
  deletePhoneLabel: string;
}

class PhonesFilter {
  search?: string;
  line_type?: LineType;
  page?: string;
  per_page: string = '50';

  toString(): string {
    const arr: string[] = [];
    if (this.page) arr.push('page=' + this.page);
    arr.push('per_page=' + this.per_page);
    if (this.search) arr.push('search=' + encodeURIComponent(this.search));
    if (this.line_type) arr.push('line_type=' + this.line_type);
    return arr.join('&');
  }
}

const LINE_TYPE_BADGE: Record<string, string> = {
  wireless: styles.BadgeWireless,
  voip: styles.BadgeVoip,
  landline: styles.BadgeLandline,
  other: styles.BadgeOther,
};

export default class PhonesComponent extends React.Component<any, IState> {

  state: IState = {
    phones: [],
    pagination: null,
    backdrop: true,
    alert: false,
    errorAlert: false,
    errors: [],
    filter: new PhonesFilter(),
    searchDisplay: '',
    editShow: false,
    currentPhone: CCPhone.empty(),
    deleteConfirmShow: false,
    deletePhoneId: 0,
    deletePhoneLabel: '',
  };

  timeoutID: number | null = null;

  componentDidMount() {
    document.title = 'Phones';
    this.load();
  }

  load() {
    this.setState({ backdrop: true });
    PhonesActions.getPhones(this.state.filter.toString())
      .then((data: IAxiosCCPhones) => {
        const phones = data.data.data.map((d: ICCPhone) => new CCPhone(d));
        const pages = new Pages(data.data.per_page, data.data.current_page, data.data.last_page);
        this.setState({ phones, pagination: pages, backdrop: false });
      })
      .catch((err: any) => {
        const errors = err?.response?.data?.errors ?? [err?.response?.data?.error ?? 'Unknown error'];
        this.setState({ errorAlert: true, backdrop: false, errors });
      });
  }

  handleChangePage(page: number) {
    const filter = this.state.filter;
    filter.page = page.toString();
    this.setState({ filter }, () => this.load());
  }

  handleSearch(masked: string, digits: string) {
    const filter = this.state.filter;
    filter.search = digits || undefined;
    filter.page = undefined;
    this.setState({ filter, searchDisplay: masked });
    if (digits.length === 0 || digits.length === 10) {
      if (this.timeoutID) clearTimeout(this.timeoutID);
      this.load();
    }
  }

  handleLineType(value: string) {
    const filter = this.state.filter;
    filter.line_type = (value as LineType) || undefined;
    filter.page = undefined;
    this.setState({ filter }, () => this.load());
  }

  openCreate() {
    this.setState({ currentPhone: CCPhone.empty(), editShow: true });
  }

  openEdit(phone: CCPhone) {
    this.setState({ currentPhone: phone, editShow: true });
  }

  handleSaved() {
    this.setState({ editShow: false, alert: true });
    this.load();
  }

  openDeleteConfirm(phone: CCPhone) {
    this.setState({
      deleteConfirmShow: true,
      deletePhoneId: phone.Id,
      deletePhoneLabel: phone.friendly(),
    });
  }

  deletePhone() {
    PhonesActions.deletePhone(this.state.deletePhoneId)
      .then((res) => {
        this.setState({ deleteConfirmShow: false });
        if (res === true) {
          this.load();
        } else {
          const e = res as any;
          this.setState({ errorAlert: true, errors: e.errors ?? [e.error] });
        }
      });
  }

  lineTypeBadge(lineType: LineType | null): React.ReactElement {
    if (!lineType) return <span>—</span>;
    const cls = LINE_TYPE_BADGE[lineType] ?? '';
    return <span className={`${styles.Badge} ${cls}`}>{lineType}</span>;
  }

  render() {
    const { phones, pagination, filter } = this.state;

    return (
      <div>
        <Container maxWidth="lg">
          <Grid container spacing={1}>
            <Grid item xs={12} className={mainStyles.WorkerSkillsBack}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <div className={mainStyles.Header}>&nbsp;</div>
                  <div className={mainStyles.AgentName}>Phones Management</div>
                </Grid>
              </Grid>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={3}>
                  <PhoneMaskInput
                    className={styles.FilterControl}
                    value={this.state.searchDisplay}
                    onChange={(masked, digits) => this.handleSearch(masked, digits)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <select
                    className={styles.FilterControl}
                    value={filter.line_type ?? ''}
                    onChange={(e) => this.handleLineType(e.target.value)}
                  >
                    <option value="">(any line type)</option>
                    <option value="landline">landline</option>
                    <option value="wireless">wireless</option>
                    <option value="voip">voip</option>
                    <option value="other">other</option>
                  </select>
                </Grid>
                <Grid item xs={12} md={3}>
                  <BlueInverseBtn onClick={() => this.openCreate()} variant="contained">
                    <Add /> Add Phone
                  </BlueInverseBtn>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} className={mainStyles.WorkerSkillsBack}>
              <TableContainer component={Paper}>
                <Table aria-label="phones table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">Phone</TableCell>
                      <TableCell align="left">Line Type</TableCell>
                      <TableCell align="left">Carrier</TableCell>
                      <TableCell align="left">Lookup Date</TableCell>
                      <TableCell align="left">Created At</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {phones.map((row) => (
                      <TableRow key={row.Id}>
                        <TableCell align="left">
                          <div>{row.friendly()}</div>
                          <div className={styles.SubText}>{row.Phone}</div>
                        </TableCell>
                        <TableCell align="left">
                          {this.lineTypeBadge(row.LineType)}
                          {row.LineTypeDetail && (
                            <div className={styles.SubText}>{row.LineTypeDetail}</div>
                          )}
                        </TableCell>
                        <TableCell align="left">{row.CarrierName ?? '—'}</TableCell>
                        <TableCell align="left">{row.LookupDate ?? '—'}</TableCell>
                        <TableCell align="left">{row.CreatedAt}</TableCell>
                        <TableCell align="right">
                          <Button color="primary" onClick={() => this.openEdit(row)}>
                            <CreateIcon />
                          </Button>
                          <Button color="secondary" onClick={() => this.openDeleteConfirm(row)}>
                            <DeleteForever />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {phones.length === 0 && !this.state.backdrop && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">No phones found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {pagination !== null && (
                <Grid item xs={12}>
                  <Pagination
                    className={styles.Pagination}
                    count={pagination.LastPage}
                    page={pagination.CurrentPage}
                    onChange={(_e, page) => this.handleChangePage(page)}
                    variant="outlined"
                    shape="rounded"
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Container>

        <PhoneEditComponent
          phone={this.state.currentPhone}
          editShow={this.state.editShow}
          toggleHandle={() => this.setState({ editShow: false })}
          savedHandle={() => this.handleSaved()}
          onError={(errors) => this.setState({ errorAlert: true, errors })}
        />

        <ConfirmComponent
          show={this.state.deleteConfirmShow}
          title={'Delete ' + this.state.deletePhoneLabel}
          bodyText="Are you sure you want to delete this phone?"
          close={() => this.setState({ deleteConfirmShow: false })}
          confirm={() => this.deletePhone()}
        />

        <Snackbar
          open={this.state.alert}
          autoHideDuration={4000}
          onClose={() => this.setState({ alert: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={() => this.setState({ alert: false })} severity="success" variant="filled">
            Saved!
          </Alert>
        </Snackbar>

        <Snackbar
          open={this.state.errorAlert}
          autoHideDuration={5000}
          onClose={() => this.setState({ errorAlert: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={() => this.setState({ errorAlert: false })} severity="error" variant="filled">
            Error<br />
            <div className={styles.AlertError}>{this.state.errors.join(', ')}</div>
          </Alert>
        </Snackbar>

        <Backdrop className={styles.Backdrop} open={this.state.backdrop}>
          <CircularProgress className={styles.BackdropProgress} />&nbsp;&nbsp;
          <p className={styles.BackdropProgress}>Loading...</p>
        </Backdrop>
      </div>
    );
  }
}
